/**
 * Debt trend arithmetic (DEC-184, UI_SDD §R.9). Pure functions, so the headline sentence,
 * the range cut and the hover box text are tested directly rather than through recharts.
 *
 * Every sentence here is a fixed template over computed numbers. None of it is generated:
 * the AI narrative is a separate, attributed line on the page (DEC-120).
 */
import type { TrendPoint, TrendProjection, TrendProjectionMonth } from '../../types/api'

export type RangeKey = '6m' | 'ytd' | '12m' | 'eoy'

export const RANGE_KEYS: RangeKey[] = ['6m', 'ytd', '12m', 'eoy']

export const RANGE_LABELS: Record<RangeKey, string> = {
  '6m': '6 months',
  ytd: 'Year to date',
  '12m': '12 months',
  eoy: 'End of year',
}

/** The eyebrow wording: "Debt trend · last 6 months". */
export const RANGE_EYEBROWS: Record<RangeKey, string> = {
  '6m': 'last 6 months',
  ytd: 'year to date',
  '12m': 'last 12 months',
  eoy: 'to the end of the year',
}

const MONTHS_LONG = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** Parsed from the ISO string by hand: `new Date('2026-10-01')` is UTC midnight, which is
 *  the previous day, and the previous month, west of Greenwich. */
export function yearMonth(periodStart: string): { year: number; month: number } {
  const [y, m] = periodStart.split('-')
  return { year: Number(y), month: Number(m) - 1 }
}

export const monthLong = (periodStart: string) => MONTHS_LONG[yearMonth(periodStart).month]
export const monthShort = (periodStart: string) => MONTHS_SHORT[yearMonth(periodStart).month]

/** One row of the chart: a measured period, a gap, or a projected month. */
export interface TrendRow {
  key: string
  period_start: string
  period_label: string
  /** The measured Debt Index; null on a gap and on a projected month. */
  measured: number | null
  /** The projected Debt Index; also set on the latest measured row, so the dashed line
   *  starts where the measured one ends. */
  projected: number | null
  isProjected: boolean
  isLatest: boolean
  point: TrendPoint | null
  /** The measured point before this one in the full series, for "+12 vs Sep". */
  previous: TrendPoint | null
  previousValue: number | null
}

export const isMeasured = (p: TrendPoint) => !p.is_gap && p.debt_index != null

export interface RangeCut {
  points: TrendPoint[]
  projection: TrendProjectionMonth[]
}

/**
 * Cut the full monthly series to a range. The series runs to the current month (the
 * server enumerates every period up to now), so "the latest period" is its last element.
 */
export function cutRange(
  series: TrendPoint[],
  range: RangeKey,
  projection: TrendProjection | null | undefined,
): RangeCut {
  if (series.length === 0) return { points: [], projection: [] }
  const last = series[series.length - 1]
  const year = yearMonth(last.period_start).year
  const thisYear = series.filter((p) => yearMonth(p.period_start).year === year)
  switch (range) {
    case '6m':
      return { points: series.slice(-6), projection: [] }
    case '12m':
      return { points: series.slice(-12), projection: [] }
    case 'ytd':
      return { points: thisYear, projection: [] }
    case 'eoy':
      return {
        points: thisYear,
        projection: (projection?.months ?? []).filter((m) => m.period_start > last.period_start),
      }
  }
}

export function buildRows(series: TrendPoint[], cut: RangeCut): TrendRow[] {
  const measuredInRange = cut.points.filter(isMeasured)
  const latest = measuredInRange[measuredInRange.length - 1]
  const showProjection = cut.projection.length > 0
  const rows: TrendRow[] = cut.points.map((p) => {
    const idx = series.indexOf(p)
    const previous =
      series
        .slice(0, idx < 0 ? 0 : idx)
        .filter(isMeasured)
        .slice(-1)[0] ?? null
    const isLatest = p === latest
    return {
      key: p.period_start,
      period_start: p.period_start,
      period_label: p.period_label,
      measured: isMeasured(p) ? p.debt_index : null,
      projected: showProjection && isLatest ? p.debt_index : null,
      isProjected: false,
      isLatest,
      point: p,
      previous,
      previousValue: previous?.debt_index ?? null,
    }
  })
  let prevValue = latest?.debt_index ?? null
  let prevStart = latest?.period_start ?? null
  for (const m of cut.projection) {
    rows.push({
      key: m.period_start,
      period_start: m.period_start,
      period_label: m.period_label,
      measured: null,
      projected: m.debt_index,
      isProjected: true,
      isLatest: false,
      point: null,
      previous: prevStart
        ? ({ period_start: prevStart, debt_index: prevValue } as TrendPoint)
        : null,
      previousValue: prevValue,
    })
    if (m.debt_index != null) {
      prevValue = m.debt_index
      prevStart = m.period_start
    }
  }
  return rows
}

// ---------------------------------------------------------------------------
// Numbers and sentences
// ---------------------------------------------------------------------------

export const formatIndex = (v: number) => String(Math.round(v))

/** Percent change, or null when it cannot be computed honestly. */
export function percentChange(from: number | null, to: number | null): number | null {
  if (from == null || to == null || from === 0) return null
  return ((to - from) / from) * 100
}

/** "May", or "November 2025" when the start sits in a different year from the end. */
export function sinceLabel(first: TrendPoint, last: TrendPoint): string {
  const a = yearMonth(first.period_start)
  const b = yearMonth(last.period_start)
  return a.year === b.year ? MONTHS_LONG[a.month] : `${MONTHS_LONG[a.month]} ${a.year}`
}

/** The headline, from a fixed template. Never from the model. */
export function headline(
  measured: TrendPoint[],
  projection: TrendProjectionMonth[],
  range: RangeKey,
): string {
  const ends = projection.filter((m) => m.debt_index != null)
  if (range === 'eoy' && ends.length > 0) {
    const end = ends[ends.length - 1]
    return `If nothing is fixed, debt reaches ${formatIndex(end.debt_index as number)} by ${monthLong(end.period_start)}.`
  }
  if (measured.length < 2) return 'There is no direction to report for this range yet.'
  const first = measured[0]
  const last = measured[measured.length - 1]
  const pct = percentChange(first.debt_index, last.debt_index)
  const since = sinceLabel(first, last)
  const rounded = pct == null ? 0 : Math.round(Math.abs(pct))
  if (pct == null || rounded === 0) return `Technical debt is flat since ${since}.`
  return `Technical debt is ${pct > 0 ? 'up' : 'down'} ${rounded}% since ${since}.`
}

export type Direction = 'up' | 'down' | 'flat'

/** Debt going up is bad: up takes the bad tone, down the good one. */
export const toneFor = (dir: Direction): 'bad' | 'good' | 'neutral' =>
  dir === 'up' ? 'bad' : dir === 'down' ? 'good' : 'neutral'

export function direction(delta: number | null, epsilon = 0.5): Direction {
  if (delta == null || Math.abs(delta) < epsilon) return 'flat'
  return delta > 0 ? 'up' : 'down'
}

/** "↑ 12%", "↓ 4%", "No change". */
export function percentPill(from: number | null, to: number | null): { text: string; dir: Direction } {
  const pct = percentChange(from, to)
  const dir = pct == null ? 'flat' : direction(pct)
  if (pct == null || dir === 'flat') return { text: 'No change', dir: 'flat' }
  return { text: `${dir === 'up' ? '↑' : '↓'} ${Math.round(Math.abs(pct))}%`, dir }
}

/** The first sentence of a paragraph, for the line under the headline. */
export function firstSentence(text: string | null | undefined): string | null {
  const t = (text ?? '').trim()
  if (!t) return null
  const m = t.match(/^[\s\S]*?[.!?](?=\s|$)/)
  return (m ? m[0] : t).trim()
}

/** "May to Oct 2026", or "Nov 2025 to Oct 2026" across a year. */
export function spanLabel(rows: TrendRow[]): string {
  if (rows.length === 0) return ''
  const a = yearMonth(rows[0].period_start)
  const b = yearMonth(rows[rows.length - 1].period_start)
  if (rows.length === 1) return `${MONTHS_SHORT[a.month]} ${a.year}`
  return a.year === b.year
    ? `${MONTHS_SHORT[a.month]} to ${MONTHS_SHORT[b.month]} ${b.year}`
    : `${MONTHS_SHORT[a.month]} ${a.year} to ${MONTHS_SHORT[b.month]} ${b.year}`
}

/** Axis tick: "Oct", with a two-digit year on the first tick and on every January. */
export function tickLabel(periodStart: string, isFirst: boolean): string {
  const { year, month } = yearMonth(periodStart)
  return isFirst || month === 0 ? `${MONTHS_SHORT[month]} ${String(year).slice(2)}` : MONTHS_SHORT[month]
}

export interface Movement {
  /** Points in the range whose movement could be computed. */
  available: number
  newCount: number
  fixedCount: number
}

/** New and fixed, summed over the range's points that have a gap available. A point
 *  without one contributes nothing, never a zero that reads as measured. */
export function movement(points: TrendPoint[]): Movement {
  const usable = points.filter((p) => isMeasured(p) && p.gap_available === true)
  return {
    available: usable.length,
    newCount: usable.reduce((a, p) => a + (p.new_findings ?? 0), 0),
    fixedCount: usable.reduce((a, p) => a + (p.fixed_findings ?? 0), 0),
  }
}

// ---------------------------------------------------------------------------
// Hover box
// ---------------------------------------------------------------------------

export interface TooltipLines {
  title: string
  /** The Debt Index, or null on a gap. */
  value: string | null
  detail: string
  /** Only when movement is not available: why. */
  reason: string | null
  /** The point's own note (carried repositories, partial movement coverage), verbatim. */
  note: string | null
}

function signed(delta: number): string {
  const r = Math.round(delta)
  if (r === 0) return 'No change'
  return r > 0 ? `+${r}` : `-${Math.abs(r)}`
}

/**
 * The three lines of the quiet hover box: "October 2026, so far" / "Debt Index : 112" /
 * "+12 vs Sep · 5 new · 2 fixed". A month with no gap available says so and why, rather
 * than showing zeros.
 */
export function tooltipLines(row: TrendRow): TooltipLines {
  const { year } = yearMonth(row.period_start)
  const base = `${monthLong(row.period_start)} ${year}`
  const value = row.isProjected ? row.projected : row.measured
  const p = row.point

  const suffix = row.isProjected ? ', projected' : p?.is_incomplete ? ', so far' : ''
  const title = base + suffix
  const note = p?.note ?? null

  if (value == null) {
    return {
      title,
      value: null,
      detail: row.isProjected
        ? 'No projected value for this month.'
        : 'No scan this month, so nothing was measured.',
      reason: null,
      note: p?.is_gap ? null : note,
    }
  }

  const step =
    row.previousValue != null && row.previous
      ? `${signed(value - row.previousValue)} vs ${monthShort(row.previous.period_start)}`
      : 'First measured month'

  if (row.isProjected) {
    return { title, value: formatIndex(value), detail: `${step} · projected`, reason: null, note: null }
  }

  if (p?.gap_available === true) {
    return {
      title,
      value: formatIndex(value),
      detail: `${step} · ${p.new_findings ?? 0} new · ${p.fixed_findings ?? 0} fixed`,
      reason: null,
      note,
    }
  }
  return {
    title,
    value: formatIndex(value),
    detail: `${step} · movement not available`,
    reason: p?.gap_unavailable_reason ?? null,
    note,
  }
}
